import React from 'react'
import tw from 'twin.macro'
import { SlCheckbox, SlIcon } from '@shoelace-style/shoelace/dist/react'
import { Card } from '@nominex/dex-uikit'
import { ChainId, Currency } from '@nominex/nomiswap-sdk'

import useTheme from 'hooks/useTheme'
import { useTranslation } from 'contexts/Localization'
import { Field } from 'state/mint/actions'
import { formatNumber } from 'utils/formatters'
import { getBalanceAmount } from 'utils/formatBalance'
import { useWagmi } from 'libs/wagmi'

const ColoredWrapper = tw.div`
  bg-[#131d49]
`

const PieChart: React.FC<{ share: number }> = ({ share }) => {
  const { theme } = useTheme()

  return (
    <ColoredWrapper className="absolute flex items-center justify-center self-center justify-self-center square-32 rounded-full">
      <div
        className="square-24 rounded-full"
        style={{ background: `conic-gradient(${theme.colors.pink500} ${share * 100}%, ${theme.colors.aqua500} 0)` }}
      />
    </ColoredWrapper>
  )
}

interface CustomProportionWidgetProps {
  addInCustomProportion: boolean
  onAddInCustomProportionChange: (newValue: boolean) => void
  currencies: { [field in Field]?: Currency }
  amountAToSwap: string
  amountBToSwap: string
  amountAToAdd: string
  amountBToAdd: string
  amountAReceived: string
  amountBReceived: string
  poolShareA: number
  poolShareB: number
  totalAmountInUsdt: number
}

const CustomProportionWidget: React.FC<CustomProportionWidgetProps> = ({
  addInCustomProportion,
  onAddInCustomProportionChange,
  currencies,
  amountAToSwap,
  amountBToSwap,
  amountAToAdd,
  amountBToAdd,
  amountAReceived,
  amountBReceived,
  poolShareA,
  poolShareB,
  totalAmountInUsdt,
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { chainId } = useWagmi()
  const currencyA = currencies[Field.CURRENCY_A]
  const currencyB = currencies[Field.CURRENCY_B]
  const balanceAmountAToSwap = getBalanceAmount(amountAToSwap, currencyA?.decimals).toNumber()
  const balanceAmountBToSwap = getBalanceAmount(amountBToSwap, currencyB?.decimals).toNumber()
  const balanceAmountAToAdd = getBalanceAmount(amountAToAdd, currencyA?.decimals).toNumber()
  const balanceAmountBToAdd = getBalanceAmount(amountBToAdd, currencyB?.decimals).toNumber()
  const balanceAmountAReceived = getBalanceAmount(amountAReceived, currencyA?.decimals).toNumber()
  const balanceAmountBReceived = getBalanceAmount(amountBReceived, currencyB?.decimals).toNumber()

  return (
    <>
      <Card background={`${theme.colors.blue500}1A`} withoutBorder>
        <div className="flex flex-col gap-4 p-16">
          <SlCheckbox
            checked={addInCustomProportion}
            onSlChange={() => onAddInCustomProportionChange(!addInCustomProportion)}
          >
            <p className="text-14 text-blue-500 font-semibold ml-4">{t('Add tokens in custom proportion')}</p>
          </SlCheckbox>

          <p className="text-14 text-blue-500 opacity-60 ml-32">
            {t('Add liquidity with one token or any proportion, without manual swapping or token balancing.')}
          </p>
        </div>
      </Card>

      {addInCustomProportion && !!totalAmountInUsdt && (
        <Card isActive>
          <ColoredWrapper className="flex flex-col gap-12 p-16">
            {chainId === ChainId.BSC && ( // TODO: Temporary disabled for Ethereum
              <div className="flex justify-between">
                <p className="text-14 font-semibold">{t('You will add to the pool')}</p>
                <p className="text-14 text-dark-gray-100 notranslate" translate="no">
                  ≈ {formatNumber(totalAmountInUsdt, 'toUSD')}
                </p>
              </div>
            )}

            <div className="flex items-center justify-center gap-12">
              <Card className="w-full" background={`${theme.colors.aqua500}26`} withoutBorder>
                <div className="p-12">
                  <p className="text-12 text-aqua">
                    {t('1st token %percent%', { percent: formatNumber(poolShareA, 'percentage') })}
                  </p>
                  <p className="font-semibold notranslate" translate="no">
                    {formatNumber(balanceAmountAToAdd)} {currencyA?.symbol}
                  </p>
                </div>
              </Card>

              <Card className="w-full" background={`${theme.colors.pink500}26`} withoutBorder>
                <div className="p-12">
                  <p className="text-12 text-pink">
                    {t('2nd token %percent%', { percent: formatNumber(poolShareB, 'percentage') })}
                  </p>
                  <p className="font-semibold notranslate" translate="no">
                    {formatNumber(balanceAmountBToAdd)} {currencyB?.symbol}
                  </p>
                </div>
              </Card>

              <PieChart share={poolShareB} />
            </div>

            <p className="text-14">{t('The tokens will be automatically rebalanced before adding liquidity.')}</p>

            <div className="flex items-center gap-8">
              <div className="flex items-center justify-center h-20 w-20 rounded-full gradient-dark-blue">
                <SlIcon className="square-12" name="exchange" library="unicons" />
              </div>

              <div
                className={`notranslate flex items-center ${
                  balanceAmountAToSwap > 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
                translate="no"
              >
                <p className="text-14">
                  {formatNumber(balanceAmountAToSwap > 0 ? balanceAmountAToSwap : balanceAmountAReceived)}{' '}
                  {currencyA?.symbol}
                </p>

                <SlIcon className="text-blue-500 square-20" name="arrow-right" library="unicons" />

                <p className="text-14">
                  {formatNumber(balanceAmountBToSwap > 0 ? balanceAmountBToSwap : balanceAmountBReceived)}{' '}
                  {currencyB?.symbol}
                </p>
              </div>
            </div>
          </ColoredWrapper>
        </Card>
      )}
    </>
  )
}

export default CustomProportionWidget
